import { useTranslation } from "react-i18next";
import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import Layout from 'Components/Layout';
import PageHeader from 'Components/PageHeader/PageHeader';
import { setAdminMode as setIsOpen } from 'store/slices/businessSlice';
import { refetchUser } from 'store/slices/authSlice';
import ProfileList from 'Components/Personal/ProfileList'; // Main MyProfile Component
import MergedContactsViewer from 'Components/Personal/MergedContactsViewer';

function MyProfile({
  isAuthenticated,
  currentUser,
  setIsOpen,
  refetchUser,
  optimisticUpdateInProgress
}) {
  const {
    t
  } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [viewMode, setViewMode] = useState('properties'); // 'properties' | 'mergedContacts'
  const [hasRefetched, setHasRefetched] = useState(false);
  const isMountedRef = useRef(true);
  const currentUserId = currentUser?.id;
  const profileUpdatedAt = currentUser?.profile?.updatedAt;
  const profilePropertiesLength = currentUser?.profile?.properties?.length;
  const currentUserProfile = currentUser?.profile; // A simple way to detect changes in properties without a complex expression in the array

  const profilePropertiesMap = currentUser?.profile?.properties?.map(p => p.objectId).join(',');
  useEffect(() => {
    console.log('MyProfile: useEffect triggered', {
      isAuthenticated,
      currentUser
    });

    if (!isAuthenticated || !currentUser) {
      setError('Not logged in');
      setLoading(false);
      return;
    } // Check if profile has properties, if not, refetch user data
    // But don't refetch if we have an optimistic update in progress or already refetched


    if (!optimisticUpdateInProgress && !hasRefetched && (!currentUser.profile || !currentUser.profile.properties || currentUser.profile.properties.length === 0)) {
      console.log('MyProfile: No profile properties found, refetching user data...');
      setHasRefetched(true); // Prevent infinite refetching

      refetchUser().then(updatedUser => {
        if (!isMountedRef.current) return; // Prevent state update on unmounted component

        console.log('MyProfile: Refetched user data', {
          updatedUser,
          profile: updatedUser?.profile
        });
        setData({
          success: true,
          user: updatedUser,
          profile: updatedUser?.profile || currentUser?.profile || {
            properties: []
          }
        });
        setLoading(false);
      }).catch(err => {
        if (!isMountedRef.current) return; // Prevent state update on unmounted component

        console.error('MyProfile: Error refetching user:', err); // On error, still try to use existing data from Redux

        setData({
          success: true,
          user: currentUser,
          profile: currentUser?.profile || {
            properties: []
          }
        });
        setLoading(false);
      });
    } else {
      // Use data from Redux store - this will update when currentUser.profile changes
      console.log('MyProfile: Setting data from Redux store', {
        currentUser,
        profile: currentUser.profile,
        propertiesCount: currentUser?.profile?.properties?.length,
        propertyNames: currentUser?.profile?.properties?.map(p => p.name)
      }); // Create new object references to ensure React detects changes

      setData({
        success: true,
        user: { ...currentUser
        },
        profile: currentUser?.profile ? { ...currentUser.profile,
          properties: currentUser.profile.properties ? [...currentUser.profile.properties] : []
        } : {
          properties: []
        }
      });
      setLoading(false);
    }
  }, [isAuthenticated, currentUserId, profileUpdatedAt, profilePropertiesLength, currentUserProfile, profilePropertiesMap, optimisticUpdateInProgress, currentUser, hasRefetched, refetchUser]); // Cleanup function to prevent memory leaks

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  if (loading) return <Layout>
      <Helmet>
        <title>{t("MY PROFILE - HeresMyInfo.com")}</title>
      </Helmet>
      <PageHeader title={t("MY PROFILE")} description="This information is never shared with anyone until you put it in a Card." />
      <div style={{
      padding: 20
    }}><h2>{t("MY PROFILE")}</h2><p>{t("Loading...")}</p></div>
    </Layout>;
  if (error) return <Layout>
      <Helmet>
        <title>{t("MY PROFILE - HeresMyInfo.com")}</title>
      </Helmet>
      <PageHeader title={t("MY PROFILE")} description="This information is never shared with anyone until you put it in a Card." />
      <div style={{
      padding: 20
    }}><h2>{t("MY PROFILE")}</h2><p style={{
        color: 'red'
      }}>{error}</p></div>
    </Layout>; // Create a profile object that matches the expected structure

  const mergedContactsList = data?.profile?.mergedContacts || data?.user?.mergedContacts || currentUser?.profile?.mergedContacts || currentUser?.mergedContacts || [];
  const profile = {
    properties: data?.profile?.properties || [],
    mergedContacts: mergedContactsList
  };

  return <Layout>
      <Helmet>
        <title>{t("MY PROFILE - HeresMyInfo.com")}</title>
      </Helmet>
      <PageHeader title={t("MY PROFILE")} description="This information is never shared with anyone until you put it in a Card." />

      <div style={{
      padding: 20
    }}>
        <div style={{
        marginBottom: 20,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
          <a href="https://www.heresmyinfo.com/business" className="claim-business-link" style={{
          fontSize: '16px'
        }}>{t("Do you own a business you would like to market?")}</a>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              className={`btn btn-sm ${viewMode === 'properties' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setViewMode('properties')}
              style={{ fontWeight: 600, borderRadius: '20px', padding: '6px 16px' }}
            >
              🗂 {t("Profile Properties")}
            </button>
            <button
              type="button"
              className={`btn btn-sm ${viewMode === 'mergedContacts' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setViewMode('mergedContacts')}
              style={{ fontWeight: 600, borderRadius: '20px', padding: '6px 16px' }}
            >
              👥 {t("Merged Contacts")} {mergedContactsList.length > 0 ? `(${mergedContactsList.length})` : ''}
            </button>
          </div>
        </div>

        {viewMode === 'mergedContacts' ? (
          <MergedContactsViewer mergedContacts={mergedContactsList} />
        ) : (
          profile.properties.length > 0 ? <div style={{
          marginTop: 20
        }}>
              <h3>{t("Profile Properties")}</h3>
              <ProfileList key={`profile-${data?.user?.id && String(data.user.id).trim() !== '' ? data.user.id : 'default'}-${data?.profile?.properties?.map(p => p.objectId).join(',') || 'empty'}-${data?.profile?.properties?.length || 0}`} profile={profile} setIsOpen={setIsOpen} />
            </div> : <div style={{
          marginTop: 20
        }}>
              <h3>{t("No Profile Properties")}</h3>
              <p>{t("No profile properties found. Please add some information to your profile.")}</p>
            </div>
        )}
      </div>
    </Layout>;
}

const mapStateToProps = state => ({
  isAuthenticated: state.auth.isAuthenticated,
  currentUser: state.auth.currentUser,
  optimisticUpdateInProgress: state.auth.optimisticUpdateInProgress
});

const mapDispatchToProps = dispatch => ({
  setIsOpen: toggle => dispatch(setIsOpen(toggle)),
  refetchUser: () => dispatch(refetchUser())
});

export default connect(mapStateToProps, mapDispatchToProps)(MyProfile);